/** Counter fields every reconciler reports, in display order. */
export const TALLY_FIELDS = [
  "scanned",
  "repaired",
  "errors",
  "unprocessed",
] as const;

export type TallyField = (typeof TALLY_FIELDS)[number];

export const TALLY_FIELD_LABELS: Record<TallyField, string> = {
  scanned: "Scanned",
  repaired: "Repaired",
  errors: "Errors",
  unprocessed: "Unprocessed",
};

export interface ReconcilerTally {
  name: string;
  scanned: number;
  repaired: number;
  errors: number;
  unprocessed: number;
}

/**
 * Regroups the flat `<reconciler>.<field>` counter map the runner writes back
 * into one tally per reconciler. Unrecognised keys are ignored so a reconciler
 * that reports extra fields cannot break the panel.
 */
export function groupCounters(raw: string | null): ReconcilerTally[] {
  if (!raw) return [];

  let parsed: Record<string, unknown>;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) return [];
    parsed = value as Record<string, unknown>;
  } catch {
    return [];
  }

  const byName = new Map<string, ReconcilerTally>();

  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== "number") continue;
    const separator = key.lastIndexOf(".");
    if (separator <= 0) continue;

    const name = key.slice(0, separator);
    const field = key.slice(separator + 1);
    if (!TALLY_FIELDS.includes(field as TallyField)) continue;

    const tally = byName.get(name) ?? {
      name,
      scanned: 0,
      repaired: 0,
      errors: 0,
      unprocessed: 0,
    };
    tally[field as TallyField] = value;
    byName.set(name, tally);
  }

  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Merchants covered so far by the whole sweep.
 *
 * `Job.processed` counts only the current link, while the counters are seeded
 * with everything earlier links did — so the larger of the two is the honest
 * figure whichever end of a chain the panel is looking at.
 */
export function scannedTotal(
  tallies: ReconcilerTally[],
  processed: number,
): number {
  const counted = tallies.reduce((sum, tally) => sum + tally.scanned, 0);
  return Math.max(counted, processed);
}

/**
 * Id of the job continuing this one, read from the runner's result payload.
 *
 * A sweep too large for one Lambda budget completes cleanly and enqueues a
 * successor, so a terminal job carrying this has not finished the database.
 */
export function chainedJobId(result: string | null): string | null {
  if (!result) return null;
  try {
    const parsed = JSON.parse(result) as { chainedTo?: unknown };
    return typeof parsed.chainedTo === "string" ? parsed.chainedTo : null;
  } catch {
    return null;
  }
}
